import { LevelType } from '../types/Level';
import { DraftLevel } from './types';
import { ORDERED_TYPES } from './planner';

export type TypeFilter = LevelType | 'all';

export const filterByType = (drafts: DraftLevel[], filter: TypeFilter): DraftLevel[] =>
  filter === 'all' ? drafts : drafts.filter(draft => draft.levelType === filter);

export const countByType = (drafts: DraftLevel[]): Record<LevelType, number> => {
  const counts = {} as Record<LevelType, number>;
  ORDERED_TYPES.forEach(type => { counts[type] = 0; });
  drafts.forEach(draft => { counts[draft.levelType] = (counts[draft.levelType] || 0) + 1; });
  return counts;
};

// All levels of one type together, types in the usual order, original order kept inside each type.
export const groupByType = (drafts: DraftLevel[]): DraftLevel[] => {
  const grouped: DraftLevel[] = [];
  ORDERED_TYPES.forEach(type => {
    drafts.forEach(draft => { if (draft.levelType === type) grouped.push(draft); });
  });
  return grouped;
};

export const newestFirst = (drafts: DraftLevel[]): DraftLevel[] =>
  drafts
    .map((draft, index) => ({ draft, index }))
    .sort((a, b) => (b.draft.createdAt - a.draft.createdAt) || (a.index - b.index))
    .map(entry => entry.draft);

const shuffled = <T>(items: T[], random: () => number): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

// A shuffled order that spreads the types out: the same type never comes twice in a row
// unless there are too few other levels to separate it. Approving in this order gives
// players a varied run of levels.
export const mixTypes = (drafts: DraftLevel[], random: () => number = Math.random): DraftLevel[] => {
  const piles: Partial<Record<LevelType, DraftLevel[]>> = {};
  drafts.forEach(draft => {
    (piles[draft.levelType] = piles[draft.levelType] || []).push(draft);
  });
  (Object.keys(piles) as LevelType[]).forEach(type => {
    piles[type] = shuffled(piles[type] as DraftLevel[], random);
  });

  const mixed: DraftLevel[] = [];
  let previous: LevelType | null = null;

  while (mixed.length < drafts.length) {
    const chosen: LevelType = nextType(piles, previous, random);
    mixed.push((piles[chosen] as DraftLevel[]).shift() as DraftLevel);
    previous = chosen;
  }

  return mixed;
};

// Which pile to draw from next: any type but the one just used, preferring the fullest pile
// so that a large type is spread across the whole order.
const nextType = (
  piles: Partial<Record<LevelType, DraftLevel[]>>,
  previous: LevelType | null,
  random: () => number
): LevelType => {
  const size = (type: LevelType): number => (piles[type] as DraftLevel[]).length;
  const remaining: LevelType[] = (Object.keys(piles) as LevelType[]).filter(type => size(type) > 0);
  const allowed: LevelType[] = remaining.filter(type => type !== previous);
  const pool: LevelType[] = allowed.length > 0 ? allowed : remaining;

  const largest = Math.max(...pool.map(size));
  const fullest: LevelType[] = pool.filter(type => size(type) === largest);
  return fullest[Math.floor(random() * fullest.length)];
};
